import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import appContext from "../../../../context/app/appContext";
import {
  FaCalendarAlt,
  FaUser,
  FaUsers,
  FaMapMarkerAlt,
  FaBus,
  FaChalkboardTeacher,
  FaTags,
  FaCommentDots,
  FaTimes,
} from "react-icons/fa";

const SummaryOverview = ({ data }) => {
  const [categoryOptions, setCategoryOptions] = useState([]);
  const { serverGet, serverPost } = useContext(appContext);
  const [studentFilter, setStudentFilter] = useState("");

  const confirmedStudents = () => {
    if (Object.keys(data.students.studentMap).length > 0) {
      // studentMap is non-empty
      return true;
    } else {
      // studentMap is empty
      return false;
    }
  };

  const [studModalOpen, setStudModalOpen] = useState(false);
  const [modalStudents, setModalStudents] = useState([]);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const res = await serverGet("/events/categories/all");
        const categories = res.categoryData[0] || [];
        const options = categories
          .filter((cat) => cat.is_active)
          .map((cat) => ({
            value: cat.category_id,
            label: cat.category_name,
          }));
        setCategoryOptions(options);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    getCategories();
    // eslint-disable-next-line
  }, []);

  const formatTime = (time) =>
    time ? moment(time, "HH:mm").format("h:mm A") : "—";

  const getLocationTags = (loc) =>
    [
      loc.offSite && "Off-site",
      loc.interState && "Interstate",
      loc.overSeas && "Overseas",
    ]
      .filter(Boolean)
      .join(", ");

  const getCategoryLabel = () => {
    const match = categoryOptions.find((opt) => opt.value === data.category);
    return match ? match.label : "—";
  };

  const getStudentDetails = async () => {
    try {
      const res = await serverPost(
        `/events/getMedicalDetails/${data.eventId}`,
        { formData: data }
      );

      const seen = new Set();
      let uniqueByFirst = [];

      for (const obj of res.data) {
        if (!seen.has(obj.stud_code)) {
          seen.add(obj.stud_code);
          uniqueByFirst.push(obj);
        }
      }

      // sort by year asc, then given name, then preferred name
      uniqueByFirst.sort((a, b) => {
        const sn = a.stSurname.localeCompare(b.stSurname);
        if (sn !== 0) return sn;
        const gn = a.stGvnName.localeCompare(b.stGvnName);
        if (gn !== 0) return gn;
        return a.stPrfName.localeCompare(b.stPrfName);
      });
      setModalStudents(uniqueByFirst);
      setStudModalOpen(true);
    } catch (error) {
      console.log("error getting students", error);
    }
  };

  // ===== GROUP HELPERS (new) =====
  const isGroupValue = (val = "") =>
    val.startsWith("grp_") ||
    val.startsWith("cohort_") ||
    val.startsWith("house_") ||
    val.startsWith("activity_") ||
    val.startsWith("unallocated_");

  const expandStudentValue = (val) => {
    if (!val) return [];
    if (isGroupValue(val)) {
      const group = data.students?.groupStudentsMap?.[val];
      if (Array.isArray(group)) return group; // editing case (array of stud_codes)
      if (group instanceof Set) return [...group]; // creation case (Set of stud_codes)
      return [];
    }
    // individual selection stores code directly in val
    return [val];
  };
  // =================================

  return (
    <div className=" flex flex-col max-h-[85vh]">
      {/* Sticky Heading */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between border-b-2 pb-3 mb-4 gap-4">
        {/* Event name */}
        <div className="text-2xl font-semibold text-gray-800 flex-shrink-0">
          {data.eventName}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {/* Event Summary Card */}
        <div className="bg-white border rounded shadow-sm p-4 w-full space-y-4">
          <div className="flex items-center gap-2 text-blue-700 font-semibold">
            <FaCalendarAlt />
            <div className="uppercase text-sm tracking-wide">
              Event Overview
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            {/* Left side: event core info */}
            <div className="flex-1 space-y-2">
              <div>
                <strong>Description:</strong> {data.eventDescription}
              </div>
              <div>
                <strong>Category:</strong> {getCategoryLabel()}
              </div>
              <div>
                <strong>When:</strong>{" "}
                {moment(data.preferredDate).format("DD MMM YYYY")} @{" "}
                {formatTime(data.startTime)} to{" "}
                {moment(data.endDate).format("DD MMM YYYY")} @{" "}
                {formatTime(data.endTime)}
              </div>
              {data.eventRecurs && (
                <div>
                  <strong>Recurring:</strong>{" "}
                  {data.recurrence
                    ? `${data.recurrence} until ${moment(
                        data.recurrenceEnd
                      ).format("DD MMM YYYY")}`
                    : "Yes"}
                </div>
              )}
            </div>

            {/* Right side: tags + message */}
            {(data.tags?.length > 0 || data.otherDetails?.trim()) && (
              <div className="flex-1 space-y-4">
                {data.tags?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm uppercase tracking-wide mb-1">
                      <FaTags />
                      <span>Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-[6rem] overflow-y-auto pr-2">
                      {data.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full border border-blue-300"
                        >
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {data.otherDetails?.trim() && (
                  <div>
                    <div className="flex items-center gap-2 text-gray-700 font-semibold text-sm uppercase tracking-wide mb-1">
                      <FaCommentDots />
                      <span>Message to Approvers</span>
                    </div>
                    <div className="bg-blue-50 border-l-4 border-blue-300 text-sm text-gray-800 px-4 py-3 whitespace-pre-line rounded">
                      {data.otherDetails}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {data.isMultiDay &&
          Array.isArray(data.multiDays) &&
          data.multiDays.length > 0 && (
            <div className="bg-white border rounded shadow-sm p-4 space-y-4">
              <div className="flex items-center gap-2 text-cyan-700 font-semibold mb-1">
                <FaCalendarAlt />
                <div className="uppercase text-sm tracking-wide">
                  Multi-Day Overview
                </div>
              </div>

              {data.multiDays.map((day, i) => {
                const dateLabel = moment(day.date).format("dddd, DD MMM YYYY");
                const startTime = day.startTime
                  ? moment(day.startTime, "HH:mm").format("h:mm A")
                  : "—";
                const endTime = day.endTime
                  ? moment(day.endTime, "HH:mm").format("h:mm A")
                  : "—";

                // Count distinct student codes (expand groups incl. activity_/unallocated_)
                const studentCount = new Set(
                  (day.students || []).flatMap((s) =>
                    expandStudentValue(s.value)
                  )
                ).size;

                const staffCount = data.staff?.staffUnknown
                  ? parseInt(
                      data.staff?.staffUnknownDetails?.approxNumStaff || 0
                    )
                  : day.staff?.length || 0;

                const validRatio = studentCount > 0 && staffCount > 0;
                const ratioVal = validRatio
                  ? Math.ceil(studentCount / staffCount)
                  : null;
                const ratio = ratioVal ? `1:${ratioVal}` : "—";

                return (
                  <div key={i} className="border-t pt-3 text-sm space-y-1">
                    <div className="font-medium text-gray-800">{dateLabel}</div>
                    <div>
                      <strong>Time:</strong> {startTime} – {endTime}
                    </div>
                    <div>
                      <strong>Activity:</strong> {day.activity || "—"}
                    </div>
                    <div>
                      <strong>Locations:</strong>{" "}
                      {(day.locations || []).join(", ") || "—"}
                    </div>
                    <div>
                      <strong>Students:</strong> {studentCount}
                      {validRatio && (
                        <span className="ml-2 text-gray-500">
                          (Ratio: {ratio})
                        </span>
                      )}
                      {day.students && day.students.length > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          {(() => {
                            const selectedGroups = [];
                            const individualStudents = [];
                            const seen = new Set();

                            for (const s of day.students) {
                              const val = s.value;
                              if (!val || seen.has(val)) continue;
                              seen.add(val);

                              if (isGroupValue(val)) {
                                selectedGroups.push(s.label);
                              } else {
                                individualStudents.push(s.label);
                              }
                            }

                            return (
                              <>
                                {selectedGroups.length > 0 && (
                                  <div>
                                    Groups:{" "}
                                    <span className="font-medium text-gray-700">
                                      {selectedGroups.join(", ")}
                                    </span>
                                  </div>
                                )}
                                {individualStudents.length > 0 && (
                                  <div>
                                    +{" "}
                                    <span className="font-medium text-gray-700">
                                      {individualStudents.length} individual
                                      student
                                      {individualStudents.length > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    <div>
                      <strong>Staff:</strong>{" "}
                      {data.staff?.staffUnknown
                        ? `${staffCount} (Unconfirmed)`
                        : (day.staff || []).map((s) => s.label).join(", ") ||
                          "—"}
                    </div>
                    {day.activityNotes?.trim() && (
                      <div className="bg-blue-50 border-l-4 border-blue-300 text-sm text-gray-800 px-4 py-2 rounded">
                        <span className="italic">Notes:</span>{" "}
                        {day.activityNotes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        {/* Location Card */}
        <div className="bg-white border rounded shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-purple-700 font-semibold mb-2">
            <FaMapMarkerAlt />
            <div className="uppercase text-sm tracking-wide">Locations</div>
          </div>
          <ul className="ml-4 list-disc">
            {data.locations.map((loc, i) => (
              <li key={i}>
                {loc.address}{" "}
                {getLocationTags(loc) && (
                  <span className="text-xs text-gray-500">
                    ({getLocationTags(loc)})
                  </span>
                )}
              </li>
            ))}
          </ul>
          {data.roomResource?.trim() && (
            <div>
              <strong>Resources Required:</strong>
              <div className="ml-2 whitespace-pre-line text-sm text-gray-700">
                {data.roomResource}
              </div>
            </div>
          )}
        </div>

        {/* Staff Card */}
        <div className="bg-white border rounded shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
            <FaUser />
            <div className="uppercase text-sm tracking-wide">Staff</div>
          </div>
          <div>
            <strong>Organiser:</strong> {data.staff.organiser?.label || "—"}
          </div>
          <div>
            <strong>Attending Staff:</strong>{" "}
            {data.staff.attending?.map((s) => s.label).join(", ") || "—"}
          </div>
        </div>

        {/* Logistics Card */}
        <div className="bg-white border rounded shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-pink-600 font-semibold mb-2">
            <FaBus />
            <div className="uppercase text-sm tracking-wide">Logistics</div>
          </div>
          <div>
            <strong>Transport:</strong>{" "}
            {data.transport.required
              ? Object.entries(data.transport.modes)
                  .filter(([_, v]) => v)
                  .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1))
                  .join(", ") || "None selected"
              : "No"}
          </div>
          <div>
            <strong>External Providers:</strong>{" "}
            {data.externalProvidersUsed ? "Yes" : "No"}
          </div>
          <div>
            <strong>Date Flexibility:</strong>{" "}
            {data.dateFlexibility
              ? `Yes – ${data.dateChangeReason || "No reason provided"}`
              : "No"}
          </div>
          <div>
            <strong>Parent Consent Required:</strong>{" "}
            {data.parentConsentRequired ? "Yes" : "No"}
          </div>
          <div>
            <strong>Rolls Marked:</strong> {data.rollsMarked ? "Yes" : "No"}
          </div>
        </div>

        {/* Student Attendance */}
        <div className="bg-white border rounded shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-indigo-600 font-semibold mb-2">
            <FaUsers />
            <div className="uppercase text-sm tracking-wide">
              Student Attendance
            </div>
          </div>
          {confirmedStudents() && (
            <span
              onClick={getStudentDetails}
              className="text-sm text-blue-600 hover:underline cursor-pointer"
            >
              View students
            </span>
          )}
          {data.students.noStudentsAttending ? (
            "No students attending"
          ) : data.students.notYetConfirmed ? (
            <>
              <div>
                <strong>Approx. Students:</strong>{" "}
                {data.students.notYetConfirmedDetails?.approxNumStudents || "—"}
              </div>
              <div>
                <strong>Year levels:</strong>{" "}
                {data.students.notYetConfirmedDetails?.yearLevels || "—"}
              </div>
              <div>
                <strong>Description:</strong>{" "}
                {data.students.notYetConfirmedDetails?.description || "—"}
              </div>
            </>
          ) : (
            <>
              <div>
                <strong>Selected:</strong>{" "}
                {Object.keys(data.students.studentMap || {}).length} students
              </div>
              <ul className="ml-4 mt-1 list-disc text-sm text-gray-700">
                {Object.entries(
                  Object.values(data.students.studentMap || {}).reduce(
                    (acc, student) => {
                      let raw =
                        student.stYrLvl?.toString().trim().toLowerCase() || "—";
                      if (raw === "0" || raw === "prep") raw = "Prep";
                      else if (/^year\s*\d+$/i.test(raw))
                        raw = raw.replace(/^year\s*/i, "");
                      else raw = raw.charAt(0).toUpperCase() + raw.slice(1);
                      acc[raw] = acc[raw] || [];
                      acc[raw].push(student);
                      return acc;
                    },
                    {}
                  )
                )
                  .sort(([a], [b]) => {
                    const normalize = (val) =>
                      val === "Prep" ? 0 : parseInt(val) || Infinity;
                    return normalize(a) - normalize(b);
                  })
                  .map(([year, students]) => (
                    <li key={year}>
                      {year === "Prep" ? "Prep: " : `Year ${year}: `}
                      {students.length} student{students.length > 1 ? "s" : ""}
                    </li>
                  ))}
              </ul>
            </>
          )}

          {!data.students.notYetConfirmed &&
            Array.isArray(data.students.selectedGroups) &&
            data.students.selectedGroups.length > 0 && (
              <div className="mt-3">
                <div className="text-gray-500 font-medium">Student Groups</div>
                <ul className="ml-4 list-disc">
                  {data.students.selectedGroups.map((g) => (
                    <li key={g.value}>{g.label}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>

        {/* Supervision */}
        <div className="bg-white border rounded shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
            <FaChalkboardTeacher />
            <div className="uppercase text-sm tracking-wide">
              Staff Supervision
            </div>
          </div>
          <div className="text-sm">
            {(() => {
              const studentCount = data.students.notYetConfirmed
                ? parseInt(
                    data.students.notYetConfirmedDetails?.approxNumStudents || 0
                  )
                : Object.keys(data.students.studentMap || {}).length;

              const staffUnknown = data.staff.staffUnknown;
              const staffCount = staffUnknown
                ? parseInt(data.staff.staffUnknownDetails?.approxNumStaff || 0)
                : data.staff.attending?.length || 0;

              if (!studentCount || !staffCount) return <>—</>;

              const ratio = `~1:${Math.ceil(
                studentCount / staffCount
              )} ratio (${staffCount} staff)`;

              return (
                <>
                  <div>{ratio}</div>
                  {staffUnknown && (
                    <div className="text-sm text-orange-600 mt-1">
                      <div className="text-gray-700">
                        {data.staff.staffUnknownDetails?.staffDescription ||
                          "No description provided."}
                      </div>
                      ⚠️ Staff not yet fully confirmed.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
      {studModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg h-[80vh] flex flex-col p-6 relative">
            {/* Close button */}
            <button
              onClick={() => setStudModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <FaTimes size={20} />
            </button>

            {/* Title */}
            <h4 className="text-xl font-semibold text-gray-800 mb-4">
              Students Attending
            </h4>

            {/* Filter input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search students..."
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring"
              />
            </div>

            {/* Scrollable student list */}
            <div className="overflow-y-auto max-h-[60vh]">
              {(() => {
                const term = studentFilter.toLowerCase();
                const filtered = modalStudents.filter(
                  (s) =>
                    s.stSurname.toLowerCase().includes(term) ||
                    s.stGvnName.toLowerCase().includes(term) ||
                    s.stPrfName.toLowerCase().includes(term)
                );
                const grouped = filtered.reduce((acc, s) => {
                  const yr = s.stYrLvl || "Unknown";
                  acc[yr] = acc[yr] || [];
                  acc[yr].push(s);
                  return acc;
                }, {});

                return Object.entries(grouped).map(([yr, studs]) => (
                  <div key={yr} className="mb-4">
                    <div className="text-lg font-medium text-gray-700 mb-2">
                      Year {yr}
                    </div>
                    <ul className="list-disc list-inside space-y-1">
                      {studs.map((s) => (
                        <li key={s.stud_code} className="text-gray-800">
                          {`${s.stSurname}, ${s.stPrfName}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryOverview;
